/**
 * 将点路径字符串解析为路径段数组，自动剥离 'data.' 前缀
 * @example parsePath('data.extraParams.filters.custom_filters') → ['extraParams', 'filters', 'custom_filters']
 */
export const parsePath = (path: string): string[] => {
  return path.replace(/^data\./, '').split('.');
};

/**
 * 按路径段读取对象的深层值
 */
export const readDeepValue = (
  obj: Record<string, unknown>,
  segments: string[],
): unknown => {
  return segments.reduce<unknown>((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
};

/**
 * 按路径段不可变地写入对象的深层值，返回新对象
 */
export const setDeepValue = (
  obj: Record<string, unknown>,
  segments: string[],
  value: unknown,
): Record<string, unknown> => {
  if (segments.length === 0) return obj;
  const [head, ...rest] = segments;
  if (rest.length === 0) {
    return { ...obj, [head]: value };
  }
  return {
    ...obj,
    [head]: setDeepValue(
      (obj[head] as Record<string, unknown>) ?? {},
      rest,
      value,
    ),
  };
};

/**
 * 根据 patchData 配置和搜索值，计算调用 changeData 所需的 key 和 value
 * 直接将搜索值写入目标路径
 * @param segments 由调用方传入已解析的路径段，避免重复解析
 */
export const buildPatchDataChange = (
  data: Record<string, unknown>,
  searchValue: string,
  segments: string[],
): { key: string; value: unknown; } => {
  const [changeKey, ...deepSegments] = segments;
  const existingTopValue = (data[changeKey] as Record<string, unknown>) ?? {};
  const newTopValue =
    deepSegments.length > 0
      ? setDeepValue(existingTopValue, deepSegments, searchValue)
      : searchValue;
  return { key: changeKey, value: newTopValue };
};

/**
 * 从 data 中读取当前搜索值（用于初始化输入框状态）
 * @param segments 由调用方传入已解析的路径段，避免重复解析
 */
export const getInitialSearchValue = (
  data: Record<string, unknown>,
  segments: string[],
): string => {
  const existingValue = readDeepValue(data, segments);
  return typeof existingValue === 'string' ? existingValue : '';
};
